import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, Plus } from 'lucide-react';

/**
 * Szabad szöveges mező javaslatlistával ("combobox").
 *
 * Az `Egészségügyi adatok` űrlap Állatorvos és Megnevezés mezőihez készült.
 * A két legfontosabb tulajdonsága:
 *
 *  1. **Nem korlátoz.** Bármi begépelhető, ami nincs a listában — a javaslat
 *     csak gyorsítás. Ezért NEM `<select>`: egy zárt lista megakadályozná,
 *     hogy a dolgozó új orvost vagy új beavatkozás-nevet vegyen fel.
 *  2. **Fókuszra azonnal mutat.** A teljes lista már üres mezőnél megjelenik
 *     (`Beírás nélkül is`), gépelésre pedig az ELSŐ karaktertől szűr. Így a
 *     tipikus eset — "ugyanaz a 3 orvos jár ide" — egyetlen kattintás.
 *
 * A szűrés ékezet- és kisbetű-érzéketlen: a "Dr. Kiss Péter" a "peter" és a
 * "KISS" beírására is előjön, különben a magyar nevek fele elérhetetlen lenne
 * a gyors gépeléshez.
 */

interface SuggestingInputProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  loading?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  /** Üres listánál megjelenő magyarázat (pl. "Még nincs rögzített állatorvos"). */
  emptyHint?: string;
}

/** Ékezet- és kisbetű-független összehasonlítási alak. */
const fold = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export const SuggestingInput: React.FC<SuggestingInputProps> = ({
  value, onChange, options, loading = false,
  placeholder, className = '', id, emptyHint,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurTimer = useRef<number | null>(null);

  const filtered = useMemo(() => {
    const q = fold(value.trim());
    // Üres mezőnél a teljes lista — ez a "fókuszáláskor jelenjenek meg a
    // gyakran használt orvosok" viselkedés.
    if (!q) return options;
    return options.filter(o => fold(o).includes(q));
  }, [options, value]);

  /** Pontosan egyezik-e a beírt érték valamelyik javaslattal. */
  const isExistingValue = useMemo(
    () => options.some(o => fold(o) === fold(value.trim())),
    [options, value]
  );
  const showNewValueHint = !!value.trim() && !isExistingValue;

  const pick = (option: string) => {
    onChange(option);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!open) {
      if (e.key === 'ArrowDown') { setOpen(true); e.preventDefault(); }
      return;
    }
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? filtered.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      // Csak akkor "lopjuk el" az Entert, ha tényleg ki van emelve egy elem —
      // különben a beírt új érték véletlenül lecserélődne egy javaslatra.
      e.preventDefault();
      pick(filtered[activeIndex]);
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setActiveIndex(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = window.setTimeout(() => setOpen(false), 150); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronDown className="w-4 h-4" />}
      </div>

      {open && (filtered.length > 0 || showNewValueHint || (options.length === 0 && emptyHint)) && (
        <ul className="absolute z-40 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto text-sm">
          {filtered.map((option, i) => (
            <li key={option}>
              <button
                type="button"
                // onMouseDown, nem onClick — különben a mező blur-je előbb
                // zárná be a listát, mint ahogy a kattintás megtörténne.
                onMouseDown={e => { e.preventDefault(); pick(option); }}
                className={`w-full text-left px-3 py-2 ${
                  i === activeIndex ? 'bg-teal-50 text-teal-800' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {option}
              </button>
            </li>
          ))}
          {showNewValueHint && (
            <li className="px-3 py-2 text-xs text-gray-500 flex items-center gap-1.5 border-t border-gray-100">
              <Plus className="w-3 h-3 flex-shrink-0" />
              <span>Új érték rögzítése: <span className="font-medium text-gray-700">{value.trim()}</span></span>
            </li>
          )}
          {options.length === 0 && emptyHint && !showNewValueHint && (
            <li className="px-3 py-2 text-xs text-gray-400">{emptyHint}</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SuggestingInput;
